/**
 * Utility function to generate excerpts from content
 * @param {string|object} content - The content to create excerpt from
 * @param {number} wordLimit - Number of words for the excerpt (default: 20)
 * @returns {string} - The excerpt text
 */
export const createExcerpt = (content, wordLimit = 20) => {
  if (!content) return "";

  let text = "";

  // Handle different content types
  if (typeof content === "string") {
    // Check if it's JSON string
    if (content.trim().startsWith("{") && content.trim().endsWith("}")) {
      try {
        const parsed = JSON.parse(content);
        text = extractTextFromDraft(parsed);
      } catch (error) {
        // If parsing fails, treat as plain text
        text = content;
      }
    } else {
      text = content;
    }
  } else if (typeof content === "object" && content.blocks) {
    text = extractTextFromDraft(content);
  } else {
    text = String(content);
  }

  // Remove HTML tags if any
  text = text.replace(/<[^>]*>/g, "");

  // Split into words and take the specified number
  const words = text.trim().split(/\s+/);

  if (words.length <= wordLimit) {
    return text.trim();
  }

  // Join the first 'wordLimit' words and add ellipsis
  return words.slice(0, wordLimit).join(" ") + "...";
};

/**
 * Extract text from Draft.js content structure
 * @param {object} draftContent - Draft.js content object
 * @returns {string} - Extracted text
 */
const extractTextFromDraft = (draftContent) => {
  if (!draftContent.blocks || !Array.isArray(draftContent.blocks)) {
    return "";
  }

  return draftContent.blocks
    .map((block) => block.text || "")
    .join(" ")
    .trim();
};

/**
 * Parse Draft.js content to HTML (for full content display)
 * @param {string|object} content - The content to parse
 * @returns {string} - HTML string
 */
export const parseDraftContent = (content) => {
  if (!content) return "";

  try {
    // If content is already a string, return it
    if (typeof content === "string") {
      // Check if it's JSON string
      if (content.startsWith("{") && content.endsWith("}")) {
        const parsed = JSON.parse(content);
        return parseDraftBlocks(parsed);
      }
      return content;
    }

    // If content is an object, parse it directly
    if (typeof content === "object") {
      return parseDraftBlocks(content);
    }

    return content;
  } catch (error) {
    console.error("Error parsing content:", error);
    return content;
  }
};

/**
 * Parse Draft.js blocks to HTML
 * @param {object} draftContent - Draft.js content object
 * @returns {string} - HTML string
 */
const parseDraftBlocks = (draftContent) => {
  if (!draftContent.blocks || !Array.isArray(draftContent.blocks)) {
    return "";
  }

  return draftContent.blocks
    .map((block) => {
      if (block.type === "unstyled") {
        return `<p>${block.text}</p>`;
      } else if (block.type === "header-one") {
        return `<h1>${block.text}</h1>`;
      } else if (block.type === "header-two") {
        return `<h2>${block.text}</h2>`;
      } else if (block.type === "header-three") {
        return `<h3>${block.text}</h3>`;
      } else if (block.type === "unordered-list-item") {
        return `<li>${block.text}</li>`;
      } else if (block.type === "ordered-list-item") {
        return `<li>${block.text}</li>`;
      } else {
        return `<p>${block.text}</p>`;
      }
    })
    .join("");
};
